/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { api } from "utils/api";
import { isValidCustomerId, parseCustomerId } from "utils/customerUtils";
import {
  fetchWhatsAppInbox,
  getWhatsAppApiConfigStatus,
  looksLikeRawWaId,
  markAllWhatsAppMessagesAsRead,
  formatWhatsAppStructuredAttachmentPreview,
  normalizeWaId,
} from "utils/whatsappApi";
import {
  formatCustomerContactLocalPhone,
  phoneLookupVariantsForContact,
} from "utils/phoneUtils";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";

// API utility
import { formatDateTime } from "utils/dateFormatter";

// Contact component
const Contact = ({ name, phoneNumber, multipleAccounts, additionalAccounts }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDBox lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
      <MDTypography variant="caption">{phoneNumber}</MDTypography>
      {multipleAccounts && additionalAccounts && additionalAccounts.length > 0 && (
        <MDTypography variant="caption" color="text" fontWeight="light" display="block">
          ⚠️ Multiple accounts linked to this number:
          <br />
          {additionalAccounts.map((acc) => `[${acc.code}] ${acc.name}`).join(" ")}
        </MDTypography>
      )}
    </MDBox>
  </MDBox>
);

Contact.propTypes = {
  name: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  multipleAccounts: PropTypes.bool,
  additionalAccounts: PropTypes.arrayOf(PropTypes.object),
};

Contact.defaultProps = {
  multipleAccounts: false,
  additionalAccounts: [],
};

// LastMessage component
const LastMessage = ({ text, timestamp, direction, hasUnread, unreadCount }) => (
  <MDBox display="flex" flexDirection="column">
    <MDBox display="flex" alignItems="center">
      {hasUnread && (
        <MDBox mr={1.5} display="flex" alignItems="center">
          <MDBadge
            color="error"
            variant="contained"
            size="sm"
            badgeContent={unreadCount > 0 ? unreadCount : null}
            sx={{
              minWidth: unreadCount > 0 ? "20px" : "8px",
              height: unreadCount > 0 ? "20px" : "8px",
              borderRadius: "12px",
              padding: unreadCount > 0 ? "0 6px" : 0,
              fontSize: "10px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 5px 0 rgba(233, 30, 99, 0.5)",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 0 0 0 rgba(233, 30, 99, 0.4)",
                },
                "70%": {
                  boxShadow: "0 0 0 6px rgba(233, 30, 99, 0)",
                },
                "100%": {
                  boxShadow: "0 0 0 0 rgba(233, 30, 99, 0)",
                },
              },
            }}
          />
        </MDBox>
      )}
      <MDTypography
        variant="caption"
        color="dark"
        fontWeight={hasUnread && direction === "inbound" ? "bold" : "medium"}
        sx={hasUnread && direction === "inbound" ? { color: "info.main" } : {}}
      >
        {text && text.length > 55 ? `${text.substring(0, 55)}...` : text}
      </MDTypography>
    </MDBox>
    <MDTypography variant="caption" color="secondary" fontWeight="regular">
      {formatDateTime(timestamp)} {direction === "outbound" && "✓"}
    </MDTypography>
  </MDBox>
);

LastMessage.propTypes = {
  text: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
  direction: PropTypes.oneOf(["inbound", "outbound"]).isRequired,
  hasUnread: PropTypes.bool,
  unreadCount: PropTypes.number,
};

LastMessage.defaultProps = {
  hasUnread: false,
  unreadCount: 0,
};

const resolveWaIdFromStoredPhone = (phone, variantToWaId) => {
  if (phone == null || phone === "") return null;
  const raw = String(phone).trim();
  if (variantToWaId.has(raw)) return variantToWaId.get(raw);
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  for (const [variant, waId] of variantToWaId.entries()) {
    if (String(variant).replace(/\D/g, "") === digits) return waId;
  }
  return null;
};

const CONTACT_COLUMNS = [
  "customer_contact_id",
  "customer_id",
  "name",
  "telephone",
  "whatsapp",
  "email",
];

/** Positive contact ids; customer ids may be negative (e.g. reserved account -1). */
const isValidContactId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) && n > 0;
};

const attachContactForRow = (row, variantToWaId, waToContacts) => {
  if (!row?.customer_contact_id) return;
  const waId =
    resolveWaIdFromStoredPhone(row.whatsapp, variantToWaId) ||
    resolveWaIdFromStoredPhone(row.telephone, variantToWaId);
  if (!waId) return;

  if (!waToContacts.has(waId)) waToContacts.set(waId, []);
  const rows = waToContacts.get(waId);
  const contactId = Number(row.customer_contact_id);
  if (!rows.some((entry) => Number(entry.customer_contact_id) === contactId)) {
    rows.push(row);
  }
};

const pickPrimaryContactRow = (rows, linksByContactId) => {
  if (!rows?.length) return null;
  const withLinks = rows.filter(
    (row) => (linksByContactId.get(Number(row.customer_contact_id)) || []).length > 0
  );
  if (withLinks.length > 0) return withLinks[0];
  const named = rows.find((row) => row.name && String(row.name).trim());
  return named || rows[0];
};

const collectLinkedCustomersForContacts = (contactRows, linksByContactId, customerById) => {
  const linkedCustomersMap = new Map();

  const addCustomer = (customer, customerId) => {
    const id = Number(customerId);
    if (!isValidCustomerId(id) || linkedCustomersMap.has(id)) return;
    const code = customer?.code ? String(customer.code).trim() : "";
    const name = customer?.name ? String(customer.name).trim() : "";
    linkedCustomersMap.set(id, {
      customer_id: id,
      code: code || `ID ${id}`,
      name: name || code || `Customer ${id}`,
    });
  };

  for (const row of contactRows) {
    const legacyCustomerId = Number(row.customer_id);
    if (isValidCustomerId(legacyCustomerId)) {
      addCustomer(customerById.get(legacyCustomerId), legacyCustomerId);
    }

    const linkRows = linksByContactId.get(Number(row.customer_contact_id)) || [];
    for (const link of linkRows) {
      const customerId = Number(link.customer_id);
      addCustomer(customerById.get(customerId), customerId);
    }
  }

  return [...linkedCustomersMap.values()];
};

const fetchContactLinksByContactIds = async (contactIds) => {
  if (!contactIds.length) return [];

  const linkBatches = await Promise.all(
    contactIds.map(async (contactId) => {
      try {
        const rows = await api.post("/db/customer_contact_links/filter", {
          filters: { customer_contact_id: contactId },
          columns: ["customer_id", "customer_contact_id", "get_whatsapp"],
        });
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      }
    })
  );

  const merged = new Map();
  for (const row of linkBatches.flat()) {
    const customerId = Number(row?.customer_id);
    const contactId = Number(row?.customer_contact_id);
    if (!Number.isFinite(customerId) || !Number.isFinite(contactId)) continue;
    merged.set(`${contactId}:${customerId}`, row);
  }
  return [...merged.values()];
};

const resolveInboxContactName = (inboxRow) => {
  const profile = inboxRow?.profile_name;
  if (profile && !looksLikeRawWaId(profile)) {
    return String(profile).trim();
  }
  return null;
};

const findCustomerContactsByPhoneVariants = async (variants) => {
  if (!variants.length) return [];

  const fetchForField = async (field) => {
    try {
      const rows = await api.post("/db/customer_contacts/filter", {
        filters: { [field]: variants },
        columns: CONTACT_COLUMNS,
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  };

  const merged = new Map();
  for (const row of [...(await fetchForField("whatsapp")), ...(await fetchForField("telephone"))]) {
    const contactId = Number(row?.customer_contact_id);
    if (Number.isFinite(contactId) && contactId > 0) {
      merged.set(contactId, row);
    }
  }
  return [...merged.values()];
};

/**
 * Create `customer_contacts` rows for inbox numbers with no match (telephone + whatsapp, local 0…).
 * @param {object[]} contacts
 * @param {Map<string, object>} waToContact
 * @param {Map<string, string>} variantToWaId
 */
const ensureMissingCustomerContacts = async (contacts, waToContacts, variantToWaId) => {
  const missing = contacts.filter(
    (row) => row.wa_id && !(waToContacts.get(row.wa_id)?.length > 0)
  );
  if (!missing.length) return;

  for (const inboxRow of missing) {
    const waId = inboxRow.wa_id;
    if (!waId || waToContacts.get(waId)?.length > 0) continue;

    const localPhone = formatCustomerContactLocalPhone(waId);
    if (!localPhone) continue;

    const lookupVariants = phoneLookupVariantsForContact(waId);
    const existingRows = await findCustomerContactsByPhoneVariants(lookupVariants);
    if (existingRows.length > 0) {
      for (const row of existingRows) {
        attachContactForRow(row, variantToWaId, waToContacts);
      }
      continue;
    }

    const name = resolveInboxContactName(inboxRow) || localPhone;
    const payload = {
      name,
      telephone: localPhone,
      whatsapp: localPhone,
    };

    try {
      const created = await api.post("/db/customer_contacts", payload);
      const contactId = created?.customer_contact_id ?? created?.id;
      if (contactId) {
        attachContactForRow(
          { ...payload, customer_contact_id: contactId },
          variantToWaId,
          waToContacts
        );
        continue;
      }
    } catch {
      // Unique constraint — row may have been created concurrently; re-fetch below.
    }

    const refetched = await findCustomerContactsByPhoneVariants(lookupVariants);
    for (const row of refetched) {
      attachContactForRow(row, variantToWaId, waToContacts);
    }
  }
};

/**
 * Match `wa_id` to `customer_contacts` (telephone / whatsapp), then linked customers via
 * `customer_contact_links`. Creates a contact when none exists.
 * @param {object[]} contacts
 */
export async function enrichInboxWithContactLinks(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) return contacts;

  const variantToWaId = new Map();
  for (const contact of contacts) {
    for (const variant of phoneLookupVariantsForContact(contact.wa_id)) {
      variantToWaId.set(variant, contact.wa_id);
    }
  }
  const allVariants = [...variantToWaId.keys()];
  if (allVariants.length === 0) return contacts;

  const fetchContactsForField = async (field) => {
    try {
      const rows = await api.post("/db/customer_contacts/filter", {
        filters: { [field]: allVariants },
        columns: CONTACT_COLUMNS,
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  };

  const [byWhatsapp, byTelephone] = await Promise.all([
    fetchContactsForField("whatsapp"),
    fetchContactsForField("telephone"),
  ]);

  const waToContacts = new Map();
  for (const row of [...byWhatsapp, ...byTelephone]) {
    attachContactForRow(row, variantToWaId, waToContacts);
  }

  await ensureMissingCustomerContacts(contacts, waToContacts, variantToWaId);

  const contactIds = [
    ...new Set(
      [...waToContacts.values()]
        .flat()
        .map((row) => Number(row.customer_contact_id))
        .filter(isValidContactId)
    ),
  ];

  const legacyCustomerIds = [
    ...new Set(
      [...waToContacts.values()]
        .flat()
        .map((row) => Number(row.customer_id))
        .filter(isValidCustomerId)
    ),
  ];

  const links =
    contactIds.length > 0 ? await fetchContactLinksByContactIds(contactIds) : [];

  const customerIds = [
    ...new Set([
      ...legacyCustomerIds,
      ...links.map((link) => Number(link.customer_id)).filter(isValidCustomerId),
    ]),
  ];

  let customers = [];
  if (customerIds.length > 0) {
    try {
      const customerRows = await api.post("/db/customers/filter", {
        filters: { customer_id: customerIds },
        columns: ["customer_id", "code", "name"],
      });
      customers = Array.isArray(customerRows) ? customerRows : [];
    } catch {
      customers = [];
    }
  }

  const customerById = new Map(
    customers.map((customer) => [Number(customer.customer_id), customer])
  );

  const linksByContactId = new Map();
  for (const link of links) {
    const contactId = Number(link.customer_contact_id);
    if (!linksByContactId.has(contactId)) linksByContactId.set(contactId, []);
    linksByContactId.get(contactId).push(link);
  }

  return contacts.map((inboxRow) => {
    const matchedContacts = waToContacts.get(inboxRow.wa_id) || [];
    if (!matchedContacts.length) return inboxRow;

    const primaryContact = pickPrimaryContactRow(matchedContacts, linksByContactId);
    const linkedCustomers = collectLinkedCustomersForContacts(
      matchedContacts,
      linksByContactId,
      customerById
    );

    const contactLabel =
      (primaryContact?.name && String(primaryContact.name).trim()) ||
      (inboxRow.profile_name && !looksLikeRawWaId(inboxRow.profile_name)
        ? inboxRow.profile_name
        : null);

    return {
      ...inboxRow,
      contact_name: contactLabel,
      customer_contact_id: primaryContact?.customer_contact_id ?? null,
      linked_customers: linkedCustomers,
      has_multiple_customers: linkedCustomers.length > 1,
      customers: linkedCustomers,
    };
  });
}

/**
 * Inbox rows for one CRM customer: match enriched `linked_customers` or contact WhatsApp/phone variants.
 * @param {object[]} inboxRows
 * @param {number | string} customerId
 * @param {object[]} [customerContacts] — rows from `GET /db/customers/:id/contacts`
 * @returns {object[]}
 */
export function filterInboxRowsForCustomer(inboxRows, customerId, customerContacts = []) {
  const cid = parseCustomerId(customerId);
  if (!isValidCustomerId(customerId)) return [];

  const waIds = new Set();
  for (const row of customerContacts) {
    for (const variant of phoneLookupVariantsForContact(row?.whatsapp || row?.telephone)) {
      const norm = normalizeWaId(variant);
      if (norm) waIds.add(norm);
    }
  }

  return (inboxRows || []).filter((inboxRow) => {
    const wa = normalizeWaId(inboxRow?.wa_id || "");
    if (wa && waIds.has(wa)) return true;
    const linked = inboxRow?.linked_customers || inboxRow?.customers || [];
    return linked.some((cust) => Number(cust?.customer_id) === cid);
  });
}

/**
 * Normalize linked customer contact rows to WhatsApp `wa_id` values.
 * @param {object[]} customerContacts — merged rows from `/db/customers/:id/contacts`
 * @returns {string[]}
 */
export function collectWaIdsFromCustomerContacts(customerContacts = []) {
  const waIds = new Set();
  for (const row of customerContacts) {
    for (const variant of phoneLookupVariantsForContact(row?.whatsapp || row?.telephone)) {
      const norm = normalizeWaId(variant);
      if (norm) waIds.add(norm);
    }
  }
  return [...waIds];
}

/**
 * Inbox scoped to one customer (whatsapp_API `wa_ids` filter via `customer_contact_links`).
 * @param {number | string} customerId
 * @param {{ limit?: number, fetchAll?: boolean }} [options]
 */
export async function loadWhatsAppInboxForCustomer(customerId, options = {}) {
  const cid = parseCustomerId(customerId);
  if (!isValidCustomerId(customerId)) {
    return {
      data: [],
      pagination: null,
      totalUnreadMessages: 0,
      totalConversations: 0,
      isTruncated: false,
      filter: null,
    };
  }

  const limit = Math.min(Math.max(Number(options.limit) || 500, 1), 500);
  const fetchAll = options.fetchAll !== false;
  const params = new URLSearchParams({
    limit: String(limit),
    fetch_all: fetchAll ? "true" : "false",
    customer_id: String(cid),
  });

  const body = await api.get(`/whatsapp/inbox-enriched?${params.toString()}`);
  return {
    data: Array.isArray(body?.data) ? body.data : [],
    pagination: body?.pagination ?? null,
    totalUnreadMessages: Number(body?.total_unread_messages) || 0,
    totalConversations: Number(body?.total_conversations) || 0,
    isTruncated: Boolean(body?.is_truncated),
    filter: body?.filter ?? null,
  };
}

/**
 * Inbox via main API `GET /whatsapp/inbox-enriched` (whatsapp_API inbox + bulk DB enrichment).
 * @param {{ enrichContacts?: boolean, customerId?: number | string }} [options]
 */
export async function loadWhatsAppInboxContacts(options = {}) {
  const { enrichContacts = true, customerId } = options;

  if (customerId != null && isValidCustomerId(customerId)) {
    return loadWhatsAppInboxForCustomer(customerId, options);
  }

  if (enrichContacts) {
    const body = await api.get("/whatsapp/inbox-enriched?limit=500&fetch_all=true");
    return {
      data: Array.isArray(body?.data) ? body.data : [],
      pagination: body?.pagination ?? null,
      totalUnreadMessages: Number(body?.total_unread_messages) || 0,
      totalConversations: Number(body?.total_conversations) || 0,
      isTruncated: Boolean(body?.is_truncated),
    };
  }

  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) {
    throw new Error(cfg.message);
  }

  const { data, pagination, totalUnreadMessages } = await fetchWhatsAppInbox({ limit: 500 });
  return { data, pagination, totalUnreadMessages };
}

// DataTable loader — whatsapp_API inbox + optional customer enrichment
export default async function whatsappInboxData() {
  try {
    const { data: contacts } = await loadWhatsAppInboxContacts({ enrichContacts: true });

    // Transform the data for the DataTable component
    const columns = [
      { Header: "Contact", accessor: "contactName", width: "20%" },
      { Header: "Latest Message", accessor: "latestMessageText", width: "40%" },
      { Header: "Time", accessor: "latestMessageTime", width: "15%" },
      { Header: "Status", accessor: "status", width: "15%" },
      { Header: "Actions", accessor: "action", align: "center" },
    ];

    const rows = contacts.map((contact) => {
      const waId = contact.wa_id || contact.phone_number || contact.waId;
      const contactName = contact.contact_name || contact.profile_name || waId;
      const linkedCustomers = contact.linked_customers || contact.customers || [];
      const hasUnread = contact.has_unread === true || contact.unread_count > 0;
      const unreadCount = contact.unread_count || 0;
      const latestMessageDirection = contact.latest_message_direction || "outbound";

      const rawLatest = contact.latest_message_text;
      const latestMessageTextLine =
        rawLatest != null && String(rawLatest).trim() !== ""
          ? String(rawLatest)
          : formatWhatsAppStructuredAttachmentPreview(contact.attachment) || "";

      const messageTimestamp = contact.latest_message || new Date().toISOString();

      const latestMessageTimestamp = new Date(messageTimestamp);

      // Format time for display
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let timeDisplay;
      if (latestMessageTimestamp >= today) {
        // Today: show time
        timeDisplay = latestMessageTimestamp.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else if (latestMessageTimestamp >= yesterday) {
        // Yesterday
        timeDisplay = "Yesterday";
      } else {
        // Earlier: show date
        timeDisplay = latestMessageTimestamp.toLocaleDateString([], {
          month: "short",
          day: "numeric",
        });
      }

      // Extract agent information
      const lastAgent = contact.last_agent_name || contact.last_agent || "System";

      // Create formatted row for the data table
      return {
        waId,
        contactName: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginLeft: "8px" }}>
              <strong>{contactName}</strong>
              {hasUnread && (
                <span
                  style={{
                    display: "inline-block",
                    marginLeft: "8px",
                    background: "#f44336",
                    color: "white",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    textAlign: "center",
                    lineHeight: "20px",
                    fontSize: "12px",
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        ),
        // Raw values for sorting and filtering
        contactNameRaw: contactName,
        latestMessageText: (
          <div>
            {contact.quoted_message_id ? (
              <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                ↩ Reply to earlier message
              </div>
            ) : null}
            <div style={{ marginBottom: "4px" }}>
              {latestMessageDirection === "inbound" ? "→ " : "← "}
              {latestMessageTextLine}
            </div>
            <div style={{ fontSize: "12px", color: "#999" }}>Last agent: {lastAgent}</div>
          </div>
        ),
        latestMessageTime: timeDisplay,
        status: hasUnread ? (
          <div style={{ color: "#f44336", fontWeight: "bold" }}>Unread</div>
        ) : (
          <div style={{ color: "#4caf50" }}>Read</div>
        ),
        // Keep raw values for sorting and other operations
        hasUnread,
        unreadCount,
        latestMessageDirection,
        latestMessageTimestamp,
        lastAgent,
        linkedCustomers,
        hasMultipleCustomers: contact.has_multiple_customers || linkedCustomers.length > 1,
      };
    });

    // console.log("Data transformation complete. Total rows:", rows.length);

    return {
      columns,
      rows,
    };
  } catch (error) {
    // console.error("Error fetching WhatsApp inbox data:", error);
    throw error;
  }
}

/**
 * Mark all unread inbound messages via whatsapp_API (`POST /messages/{message_id}/read` per row).
 * @param {(status: object) => void} [onProgress]
 */
export async function markAllMessagesAsRead(onProgress) {
  return markAllWhatsAppMessagesAsRead(onProgress);
}

